import React, { useState, useMemo, useEffect, useRef, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Clock, ChevronDown } from 'lucide-react';
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  Cell,
  LabelList,
  ReferenceLine,
} from 'recharts';
import { mockLCAData } from '../data/mockLCAData';
import useScrollIndicator from '../hooks/useScrollIndicator';
import ScrollIndicator from './ScrollIndicator';

const BAR_COLOR = '#10b981';
const BAR_ACTIVE = '#059669';
const BAR_NEGATIVE = '#0f766e';

function formatImpactNumber(value) {
  if (!Number.isFinite(value)) return '--';
  const abs = Math.abs(value);
  if (abs >= 1000000) return value.toLocaleString(undefined, { maximumFractionDigits: 0 });
  if (abs >= 1000) return value.toLocaleString(undefined, { maximumFractionDigits: 0 });
  if (abs >= 1) return value.toLocaleString(undefined, { maximumFractionDigits: 2 });
  if (abs === 0) return '0';
  if (abs < 0.001) return value.toExponential(2);
  return value.toLocaleString(undefined, { maximumFractionDigits: 4 });
}

function formatImpactWithUnit(value, unit, options = {}) {
  const { forceSign = false, compact = false } = options;
  if (!Number.isFinite(value)) return '--';
  const sign = forceSign && value > 0 ? '+' : '';
  const normalizedUnit = unit?.toLowerCase() ?? '';
  if (normalizedUnit.includes('kg') && Math.abs(value) >= 1000) {
    const tons = value / 1000;
    const fractionDigits = compact ? 1 : 2;
    return `${sign}${tons.toFixed(fractionDigits)} t`;
  }
  if (compact) {
    const abs = Math.abs(value);
    if (abs >= 1000) {
      return `${sign}${value.toLocaleString(undefined, { maximumFractionDigits: 0 })}${unit ? ` ${unit}` : ''}`;
    }
    return `${sign}${value.toLocaleString(undefined, { maximumFractionDigits: 1 })}${unit ? ` ${unit}` : ''}`;
  }
  return `${sign}${formatImpactNumber(value)}${unit ? ` ${unit}` : ''}`;
}

function getChartWidthPx(barCount) {
  const MIN_WIDTH = 360;
  const PER_BAR = 120;
  const SIDE_PAD = 32;
  const MAX_WIDTH = 700;
  return Math.min(MAX_WIDTH, Math.max(MIN_WIDTH, barCount * PER_BAR + SIDE_PAD));
}

function getBarSizePx(containerWidth, barCount) {
  if (!barCount || containerWidth <= 0) return 48;
  const usable = Math.max(0, containerWidth - 96);
  const perBar = usable / barCount;
  return Math.max(36, Math.min(72, Math.floor(perBar * 0.6)));
}

const StageTooltip = ({ active, payload, label }) => {
  if (!active || !payload || !payload.length) return null;
  const entry = payload[0]?.payload;
  if (!entry) return null;
  const value = entry.displayValue;
  const unit = entry.unit;
  const best = entry.__tooltipMeta?.best;
  return (
    <div className="rounded-lg border border-emerald-100 bg-white/95 shadow-sm px-3 py-2">
      <div className="text-xs text-gray-500">{label}</div>
      <div className="text-sm font-semibold text-emerald-800">{formatImpactWithUnit(value, unit)}</div>
      {Number.isFinite(best) && Number.isFinite(value) ? (
        <div className="mt-1 text-xs text-gray-500">
          {value === best
            ? 'Lowest impact stage'
            : `${formatImpactWithUnit(value - best, unit, { forceSign: true })} vs. lowest`}
        </div>
      ) : null}
    </div>
  );
};

const LifeCycleChart = ({ selectedImpact, lcaData, onSummaryData, b6OperationalImpact = 0, hasB6FromStages = false }) => {
  const [hoveredStage, setHoveredStage] = useState(null);
  const [chartType, setChartType] = useState('bar'); // 'bar' or 'pie'
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [expandPieChart, setExpandPieChart] = useState(false);
  const [activeBarIndex, setActiveBarIndex] = useState(null);
  const [includeOperationalB6, setIncludeOperationalB6] = useState(false);
  const includeB6AutoSetRef = useRef(false);
  const lastProjectIdRef = useRef(null);
  const dropdownRef = useRef(null);
  const [isNarrowViewport, setIsNarrowViewport] = useState(() => {
    if (typeof window === 'undefined') return false;
    return window.innerWidth < 768;
  });
  const {
    containerRef: scrollContainerRef,
    indicator: scrollIndicator,
    update: updateScrollIndicator,
  } = useScrollIndicator({ isEnabled: chartType === 'bar' });
  
  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setDropdownOpen(false);
      }
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    const handleResize = () => {
      setIsNarrowViewport(window.innerWidth < 768);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Life cycle stages with detailed descriptions
  // Note: These percentages MUST match the Sankey diagram sample data distribution exactly
  // Based on Sankey diagram: A1-A3: 40%, A4-A5: 20%, B1-B7: 25%, C1-C4: 10%, D: 5%
  // Total: 100% (D is treated as positive impact in sample data mode)
  const lifeCycleStages = [
    { id: 'a1-a3', name: 'A1-A3', fullName: 'Product Stage', description: 'Raw material supply, transport, and manufacturing processes', percentage: 40 },
    { id: 'a4-a5', name: 'A4-A5', fullName: 'Construction Stage', description: 'Construction and installation processes', percentage: 20 },
    { id: 'b1-b7', name: 'B1-B7', fullName: 'Use Stage', description: 'Operational use, maintenance, repair, replacement, and refurbishment', percentage: 25 },
    { id: 'c1-c4', name: 'C1-C4', fullName: 'End of Life', description: 'Demolition, transport, waste processing, and disposal', percentage: 10 },
    { id: 'd', name: 'D', fullName: 'Benefits Beyond', description: 'Environmental benefits from recycling, reuse, and energy recovery', percentage: 5 }
  ];
  
  // Use actual data if available, fallback to mock data
  const dataSource = lcaData?.isRhinoImport ? lcaData : (lcaData || mockLCAData);
  const isRhinoImport = dataSource?.isRhinoImport;
  const projectId = dataSource?.projectId || null;

  useEffect(() => {
    if (lastProjectIdRef.current !== projectId) {
      lastProjectIdRef.current = projectId;
      includeB6AutoSetRef.current = false;
    }
    if (!includeB6AutoSetRef.current && hasB6FromStages) {
      setIncludeOperationalB6(true);
      includeB6AutoSetRef.current = true;
    }
  }, [projectId, hasB6FromStages]);
  
  const chartData = useMemo(() => {
    const totalImpact = dataSource.totalImpacts[selectedImpact];
    console.log('LifeCycleChart - totalImpact:', totalImpact);
    console.log('LifeCycleChart - selectedImpact:', selectedImpact);
    console.log('LifeCycleChart - dataSource:', dataSource);
    console.log('LifeCycleChart - dataSource.totalImpacts:', dataSource.totalImpacts);
    
    // Get actual stage values from Excel LCA stages data (second worksheet)
    const stageValues = {};
    
    // Check if we have LCA stages data from Excel second worksheet
    let hasB6Stages = false;
    let b6StageTotal = 0;
    if (dataSource.lcaStagesData && Object.keys(dataSource.lcaStagesData).length > 0) {
      console.log('Found LCA stages data from Excel:', dataSource.lcaStagesData);
      
      // Aggregate individual LCA stages into grouped stages
      Object.values(dataSource.lcaStagesData).forEach(componentStages => {
        // A1-A3: Product Stage
        if (componentStages.A1 || componentStages.A2 || componentStages.A3) {
          const a1a3Value = (componentStages.A1 || 0) + (componentStages.A2 || 0) + (componentStages.A3 || 0);
          stageValues['a1-a3'] = (stageValues['a1-a3'] || 0) + a1a3Value;
        }
        
        // A4-A5: Construction Stage
        if (componentStages.A4 || componentStages.A5) {
          const a4a5Value = (componentStages.A4 || 0) + (componentStages.A5 || 0);
          stageValues['a4-a5'] = (stageValues['a4-a5'] || 0) + a4a5Value;
        }
        
        // B1-B5: Use Stage (B1-B5)
        if (componentStages.B1 || componentStages.B2 || componentStages.B3 || componentStages.B4 || componentStages.B5) {
          const b1b5Value = (componentStages.B1 || 0) + (componentStages.B2 || 0) + (componentStages.B3 || 0) + (componentStages.B4 || 0) + (componentStages.B5 || 0);
          stageValues['b1-b7'] = (stageValues['b1-b7'] || 0) + b1b5Value;
        }
        
        // B6-B7: Operational Use (B6-B7)
        if (componentStages.B6 || componentStages.B7) {
          const b6b7Value = (componentStages.B6 || 0) + (componentStages.B7 || 0);
          // Note: We're still using 'b1-b7' as the key for now, but this could be separated
          stageValues['b1-b7'] = (stageValues['b1-b7'] || 0) + b6b7Value;
          if (componentStages.B6) {
            b6StageTotal += componentStages.B6 || 0;
          }
          hasB6Stages = true;
        }
        
        // C1-C4: End of Life
        if (componentStages.C1 || componentStages.C2 || componentStages.C3 || componentStages.C4) {
          const c1c4Value = (componentStages.C1 || 0) + (componentStages.C2 || 0) + (componentStages.C3 || 0) + (componentStages.C4 || 0);
          stageValues['c1-c4'] = (stageValues['c1-c4'] || 0) + c1c4Value;
        }
        
        // D: Benefits Beyond
        if (componentStages.D) {
          stageValues['d'] = (stageValues['d'] || 0) + componentStages.D;
          console.log(`Found D stage value: ${componentStages.D}, running total: ${stageValues['d']}`);
          console.log(`D stage raw value type: ${typeof componentStages.D}, is negative: ${componentStages.D < 0}`);
        }
      });
      
      console.log('Aggregated stage values from Excel:', stageValues);
      console.log('D stage final value:', stageValues['d']);
      console.log('D stage final value type:', typeof stageValues['d']);
      console.log('D stage final value is negative:', stageValues['d'] < 0);
    } else if (!isRhinoImport) {
      console.log('No LCA stages data found, using material-specific LCA distributions (same as Sankey diagram)');
      
      // Use the same material-specific LCA distributions as the Sankey diagram
      if (dataSource.components) {
        console.log('=== DETAILED LCA CALCULATION LOG ===');
        console.log('Total impact:', totalImpact);
        
        dataSource.components.forEach(component => {
          const impact = component.impacts[selectedImpact] || 0;
          if (impact > 0) {
            // Use the EXACT same material distribution logic as Sankey diagram
            const componentMaterial = component.material;
            let materialDistribution = [];
            
            if (componentMaterial === 'Concrete') {
              materialDistribution.push({ material: 'Concrete', percentage: 0.85 });
              materialDistribution.push({ material: 'Steel', percentage: 0.15 });
            } else if (componentMaterial === 'Steel') {
              materialDistribution.push({ material: 'Steel', percentage: 0.90 });
              materialDistribution.push({ material: 'Paint', percentage: 0.10 });
            } else if (componentMaterial === 'Brick') {
              materialDistribution.push({ material: 'Ceramic', percentage: 0.70 });
              materialDistribution.push({ material: 'Concrete', percentage: 0.25 });
              materialDistribution.push({ material: 'Paint', percentage: 0.05 });
            } else if (componentMaterial === 'Glass') {
              materialDistribution.push({ material: 'Glass', percentage: 0.80 });
              materialDistribution.push({ material: 'Steel', percentage: 0.15 });
              materialDistribution.push({ material: 'Plastic', percentage: 0.05 });
            } else if (componentMaterial === 'Mineral Wool') {
              materialDistribution.push({ material: 'Insulation', percentage: 1.0 });
            } else if (componentMaterial === 'Copper') {
              materialDistribution.push({ material: 'Steel', percentage: 0.60 });
              materialDistribution.push({ material: 'Plastic', percentage: 0.40 });
            } else if (componentMaterial === 'Ceramic') {
              materialDistribution.push({ material: 'Ceramic', percentage: 0.95 });
              materialDistribution.push({ material: 'Concrete', percentage: 0.05 });
            } else if (componentMaterial === 'Gypsum') {
              materialDistribution.push({ material: 'Paint', percentage: 0.60 });
              materialDistribution.push({ material: 'Steel', percentage: 0.30 });
              materialDistribution.push({ material: 'Insulation', percentage: 0.10 });
            } else if (componentMaterial === 'Acoustic Panels') {
              materialDistribution.push({ material: 'Wood', percentage: 0.50 });
              materialDistribution.push({ material: 'Insulation', percentage: 0.30 });
              materialDistribution.push({ material: 'Steel', percentage: 0.20 });
            } else if (componentMaterial === 'Low-VOC Paint') {
              materialDistribution.push({ material: 'Paint', percentage: 1.0 });
            } else {
              // Fallback for unknown materials
              materialDistribution.push({ material: 'Steel', percentage: 1.0 });
            }
            
            console.log(`Component: ${component.name} (${component.material})`);
            console.log(`  Impact: ${impact} kg CO2-eq`);
            console.log(`  Material distribution:`, materialDistribution);
            
            // Process each material distribution
            materialDistribution.forEach(({ material: materialName, percentage }) => {
              const materialImpact = impact * percentage;
              
              // Get material-specific LCA distribution (same as Sankey diagram)
              let stageDistribution = {};
              if (materialName === 'Concrete') {
                stageDistribution = { 'a1-a3': 0.7, 'a4-a5': 0.15, 'b1-b7': 0.1, 'c1-c4': 0.04, 'd': 0.01 };
              } else if (materialName === 'Steel') {
                stageDistribution = { 'a1-a3': 0.6, 'a4-a5': 0.2, 'b1-b7': 0.15, 'c1-c4': 0.03, 'd': 0.02 };
              } else if (materialName === 'Glass') {
                stageDistribution = { 'a1-a3': 0.65, 'a4-a5': 0.18, 'b1-b7': 0.12, 'c1-c4': 0.03, 'd': 0.02 };
              } else if (materialName === 'Ceramic') {
                stageDistribution = { 'a1-a3': 0.75, 'a4-a5': 0.15, 'b1-b7': 0.08, 'c1-c4': 0.01, 'd': 0.01 };
              } else if (materialName === 'Wood') {
                stageDistribution = { 'a1-a3': 0.5, 'a4-a5': 0.2, 'b1-b7': 0.2, 'c1-c4': 0.08, 'd': 0.02 };
              } else if (materialName === 'Insulation') {
                stageDistribution = { 'a1-a3': 0.6, 'a4-a5': 0.15, 'b1-b7': 0.2, 'c1-c4': 0.03, 'd': 0.02 };
              } else if (materialName === 'Paint') {
                stageDistribution = { 'a1-a3': 0.7, 'a4-a5': 0.15, 'b1-b7': 0.1, 'c1-c4': 0.03, 'd': 0.02 };
              } else if (materialName === 'Plastic') {
                stageDistribution = { 'a1-a3': 0.65, 'a4-a5': 0.15, 'b1-b7': 0.15, 'c1-c4': 0.03, 'd': 0.02 };
              } else {
                // Default distribution for unknown materials
                stageDistribution = { 'a1-a3': 0.4, 'a4-a5': 0.2, 'b1-b7': 0.25, 'c1-c4': 0.1, 'd': 0.05 };
              }
              
              console.log(`    Material: ${materialName} (${(percentage * 100).toFixed(0)}%)`);
              console.log(`    Material impact: ${materialImpact.toFixed(1)} kg CO2-eq`);
              console.log(`    LCA distribution:`, stageDistribution);
              
              // Distribute material impact across LCA stages
              Object.entries(stageDistribution).forEach(([stageId, stagePercentage]) => {
                const stageValue = materialImpact * stagePercentage;
                stageValues[stageId] = (stageValues[stageId] || 0) + stageValue;
                console.log(`      ${stageId}: ${materialImpact.toFixed(1)} × ${stagePercentage} = ${stageValue.toFixed(1)} kg CO2-eq`);
              });
            });
            
            console.log('  Running totals:', stageValues);
          }
        });
        
        console.log('=== FINAL STAGE VALUES ===');
        Object.entries(stageValues).forEach(([stageId, value]) => {
          console.log(`${stageId}: ${value.toFixed(1)} kg CO2-eq`);
        });
        
        // Compare with expected Sankey values
        console.log('=== COMPARISON WITH S ANKEY DIAGRAM ===');
        const expectedA1A3 = 192377; // From user observation
        const calculatedA1A3 = stageValues['a1-a3'] || 0;
        const difference = Math.abs(expectedA1A3 - calculatedA1A3);
        const percentageDiff = (difference / expectedA1A3) * 100;
        console.log(`Expected A1-A3: ${expectedA1A3} kg CO2-eq`);
        console.log(`Calculated A1-A3: ${calculatedA1A3.toFixed(1)} kg CO2-eq`);
        console.log(`Difference: ${difference.toFixed(1)} kg CO2-eq (${percentageDiff.toFixed(2)}%)`);
        
        if (percentageDiff > 1) {
          console.warn(`⚠️ Significant difference detected! Check material mapping or distributions.`);
        }
        console.log('==========================');
      }
    }

    if (!includeOperationalB6 && hasB6Stages && b6StageTotal) {
      stageValues['b1-b7'] = (stageValues['b1-b7'] || 0) - Number(b6StageTotal || 0);
    }

    if (includeOperationalB6 && Number(b6OperationalImpact) > 0 && !hasB6Stages) {
      stageValues['b1-b7'] = (stageValues['b1-b7'] || 0) + Number(b6OperationalImpact);
    }
    
    const finalChartData = lifeCycleStages.map(stage => {
      // Use actual Excel value if available, otherwise use calculated material-specific value
      let displayValue;
      if (stageValues[stage.id] != null) {
        displayValue = stageValues[stage.id];
        console.log(`Using calculated value for ${stage.name}: ${displayValue}`);
      } else if (!isRhinoImport) {
        // Fallback to percentage-based calculation (should not happen with material-specific approach)
        const fallbackTotal = totalImpact || 1000;
        displayValue = fallbackTotal * (stage.percentage / 100);
        console.log(`Using fallback percentage for ${stage.name}: ${displayValue} (${stage.percentage}% of ${fallbackTotal})`);
      } else {
        displayValue = 0;
      }
      
      const absoluteValue = Math.abs(displayValue);
      console.log(`Stage ${stage.name}: Calculated value=${stageValues[stage.id]}, displayValue=${displayValue}, absoluteValue=${absoluteValue}`);
      
      return {
        ...stage,
        value: Math.max(0, displayValue),
        absoluteValue: absoluteValue,
        displayValue: displayValue,
        widthPercentage: (absoluteValue / (totalImpact || 1000)) * 100
      };
    });
    
    console.log('Final chartData:', finalChartData);
    console.log('D stage in chartData:', finalChartData.find(s => s.id === 'd'));
    
    return finalChartData;
  }, [selectedImpact, dataSource, includeOperationalB6, b6OperationalImpact]);
  
  const selectedImpactInfo = useMemo(
    () => dataSource.impactCategories.find(cat => cat.id === selectedImpact),
    [dataSource, selectedImpact]
  );
  const unitLabel = selectedImpactInfo?.unit;

  const bestStageValue = useMemo(() => {
    if (!chartData || chartData.length === 0) return null;
    return chartData.reduce((best, stage) => {
      if (!Number.isFinite(stage.displayValue)) return best;
      if (best == null) return stage.displayValue;
      return stage.displayValue < best ? stage.displayValue : best;
    }, null);
  }, [chartData]);

  const barChartData = useMemo(
    () =>
      chartData.map(stage => ({
        ...stage,
        unit: unitLabel,
        __tooltipMeta: { best: bestStageValue },
      })),
    [chartData, unitLabel, bestStageValue]
  );

  const chartWidthPx = useMemo(() => getChartWidthPx(barChartData.length), [barChartData.length]);
  const barSizePx = useMemo(
    () => getBarSizePx(chartWidthPx, barChartData.length),
    [chartWidthPx, barChartData.length]
  );
  const minScrollWidth = useMemo(() => {
    if (!isNarrowViewport) return undefined;
    return Math.max(chartWidthPx || 0, 640);
  }, [isNarrowViewport, chartWidthPx]);

  useEffect(() => {
    updateScrollIndicator();
  }, [barChartData, chartType, minScrollWidth, isNarrowViewport, updateScrollIndicator]);
  const handleBarHover = useCallback(
    (state) => {
      if (state && state.isTooltipActive && typeof state.activeTooltipIndex === 'number') {
        setActiveBarIndex(state.activeTooltipIndex);
        const payload = state.activePayload?.[0]?.payload;
        if (payload) setHoveredStage(payload);
      } else {
        setActiveBarIndex(null);
        setHoveredStage(null);
      }
    },
    []
  );

  const handleBarMouseLeave = useCallback(() => {
    setActiveBarIndex(null);
    setHoveredStage(null);
  }, []);

  // Helper function to get stage full names
  const getStageFullName = (stageId) => {
    const stageNames = {
      'A1': 'Raw Material Supply',
      'A2': 'Transport',
      'A3': 'Manufacturing',
      'A4': 'Construction',
      'A5': 'Installation',
      'B1': 'Use',
      'B2': 'Maintenance',
      'B3': 'Repair',
      'B4': 'Replacement',
      'B5': 'Refurbishment',
      'B6': 'Operational Energy Use',
      'B7': 'Operational Water Use',
      'C1': 'Demolition',
      'C2': 'Transport',
      'C3': 'Waste Processing',
      'C4': 'Disposal'
    };
    return stageNames[stageId] || stageId;
  };
  
  const totalPositiveImpact = chartData
    .filter(stage => stage.displayValue > 0)
    .reduce((sum, stage) => sum + stage.value, 0);
    
  const totalNegativeImpact = chartData
    .filter(stage => stage.displayValue < 0)
    .reduce((sum, stage) => sum + Math.abs(stage.displayValue), 0);
    
  const summaryData = useMemo(() => {
    const totalImpactWithoutBenefits = chartData
      .filter(stage => stage.id !== 'd' || stage.displayValue > 0)
      .reduce((sum, stage) => sum + stage.displayValue, 0);
    const totalBuildingImpact = dataSource.totalImpacts[selectedImpact] || 0;
    const buildingArea = dataSource.buildingInfo?.area || dataSource.building?.area || 1000;
    const carbonIntensity = buildingArea ? (totalBuildingImpact / 1000) / buildingArea : 0;
    const highestStage = chartData.reduce(
      (max, stage) => (stage.absoluteValue > max.absoluteValue ? stage : max),
      chartData[0]
    );

    return {
      totalPositiveImpact,
      totalNegativeImpact,
      totalImpactWithoutBenefits,
      carbonIntensity,
      highestStageName: highestStage?.name || '--',
      highestStageFullName: highestStage?.fullName || '--',
    };
  }, [chartData, dataSource, selectedImpact, totalNegativeImpact, totalPositiveImpact]);

  useEffect(() => {
    if (onSummaryData) onSummaryData(summaryData);
  }, [onSummaryData, summaryData]);

  return (
    <Card className="bg-white/80 backdrop-blur-sm border-0 shadow-md">
      <CardHeader>
        <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
          <CardTitle className="flex items-center text-gray-800">
            <Clock className="w-5 h-5 mr-2 text-emerald-600" />
            Life Cycle Stage Impact
          </CardTitle>
          <div className="flex items-center gap-3 flex-wrap md:flex-nowrap md:space-x-3 md:gap-0">
            <div className="relative" ref={dropdownRef}>
              <button
                onClick={() => setDropdownOpen(!dropdownOpen)}
                className="px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent bg-white flex items-center space-x-2 min-w-[100px] justify-between"
              >
                <span>{chartType === 'bar' ? 'Bar Graph' : 'Pie Chart'}</span>
                <ChevronDown className={`w-4 h-4 text-gray-500 transition-transform ${dropdownOpen ? 'rotate-180' : ''}`} />
              </button>
              
              {/* Dropdown Menu */}
              {dropdownOpen && (
                <div className="absolute top-full left-0 mt-1 w-full bg-white border border-gray-300 rounded-md shadow-lg z-10">
                  <button
                    onClick={() => {
                      setChartType('bar');
                      setDropdownOpen(false);
                    }}
                    className={`w-full px-3 py-2 text-left text-sm hover:bg-gray-100 ${chartType === 'bar' ? 'bg-emerald-50 text-emerald-700' : 'text-gray-700'}`}
                  >
                    Bar Graph
                  </button>
                  <button
                    onClick={() => {
                      setChartType('pie');
                      setDropdownOpen(false);
                    }}
                    className={`w-full px-3 py-2 text-left text-sm hover:bg-gray-100 ${chartType === 'pie' ? 'bg-emerald-50 text-emerald-700' : 'text-gray-700'}`}
                  >
                    Pie Chart
                  </button>
                </div>
              )}
            </div>
            
            {/* Expand Chart Toggle - Only show for pie chart */}
            {chartType === 'pie' && (
              <button
                onClick={() => setExpandPieChart(!expandPieChart)}
                className={`px-3 py-2 border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent transition-colors ${
                  expandPieChart 
                    ? 'border-emerald-500 bg-emerald-50 text-emerald-700' 
                    : 'border-gray-300 bg-white text-gray-700 hover:bg-gray-50'
                }`}
              >
                {expandPieChart ? 'Collapse Chart' : 'Expand Chart'}
              </button>
            )}
            <label className="flex items-center gap-2 text-xs text-gray-600">
              <input
                type="checkbox"
                className="h-4 w-4 rounded border-gray-300 text-emerald-600 focus:ring-emerald-500"
                checked={includeOperationalB6}
                disabled={!isRhinoImport && (!Number(b6OperationalImpact) && !hasB6FromStages)}
                onChange={(e) => setIncludeOperationalB6(e.target.checked)}
              />
              <span>
                Include B6 energy impact
                {isRhinoImport ? ' (use LCA tab output)' : ''}
                {!isRhinoImport && hasB6FromStages ? ' (already in Excel)' : ''}
              </span>
            </label>
          </div>
        </div>
      </CardHeader>
      <CardContent className="overflow-x-hidden">
       <div className="space-y-2">
          {/* Bar chart view styled to mirror Step 3 results */}
           <div className="w-full overflow-visible">
             <div className="relative">
              {chartType === 'bar' ? (
                <>
                  <div
                    ref={scrollContainerRef}
                    className="overflow-x-auto md:overflow-x-hidden md:overflow-visible hide-scrollbar md:scrollbar-none"
                    style={{ WebkitOverflowScrolling: 'touch' }}
                  >
                    <div
                      className="w-full pb-5 md:pb-0"
                      style={{ minWidth: isNarrowViewport ? minScrollWidth : undefined }}
                    >
                      <ResponsiveContainer width="100%" height={340}>
                        <BarChart
                          data={barChartData}
                          margin={{ top: 16, right: 8, bottom: 5, left: 10 }}
                          barCategoryGap={12}
                          barGap={4}
                          onMouseMove={handleBarHover}
                          onMouseLeave={handleBarMouseLeave}
                        >
                          <CartesianGrid stroke="#e2e8f0" strokeDasharray="4 4" vertical={false}/>
                          <XAxis
                            dataKey="name"
                            tick={{ fill: '#334155', fontSize: 12 }}
                            tickLine={false}
                            axisLine={{ stroke: '#e2e8f0' }}
                            height={22}
                            tickMargin={10}
                          />
                          <YAxis
                            tick={{ fill: '#334155', fontSize: 12, dx: -6 }}
                            tickFormatter={(value) => {
                              const formatted = formatImpactWithUnit(value, unitLabel, { forceSign: true, compact: true });
                              if (value === 0) {
                                return formatted.replace('CO2-eq', '\nCO2-eq');
                              }
                              return formatted.replace(/ /g, '\u00A0');
                            }}
                            tickLine={false}
                            axisLine={{ stroke: '#e2e8f0' }}
                            width={52}
                            tickMargin={0}
                          />
                          <Tooltip content={<StageTooltip />} />
                          <ReferenceLine y={0} stroke="#94a3b8" strokeDasharray="4 4" ifOverflow="extendDomain" />
                          <Bar
                            dataKey="displayValue"
                            isAnimationActive
                            animationDuration={600}
                            animationEasing="ease-out"
                            barSize={barSizePx}
                          >
                            <LabelList
                              dataKey="displayValue"
                              position="top"
                              formatter={(value) => formatImpactWithUnit(value, unitLabel, { forceSign: true, compact: true })}
                              style={{ fill: '#475569', fontSize: 11 }}
                            />
                            {barChartData.map((stage, index) => {
                              const isActive = index === activeBarIndex;
                              const baseColor = stage.displayValue < 0 ? BAR_NEGATIVE : BAR_COLOR;
                              const fill = isActive ? BAR_ACTIVE : baseColor;
                              const opacity = isActive ? 1 : 0.9;
                              const radius = stage.displayValue >= 0 ? [10, 10, 0, 0] : [0, 0, 10, 10];
                              return (
                                <Cell
                                  key={stage.id}
                                  fill={fill}
                                  opacity={opacity}
                                  stroke={isActive ? '#065f46' : 'none'}
                                  radius={radius}
                                />
                              );
                            })}
                          </Bar>
                        </BarChart>
                      </ResponsiveContainer>
                    </div>
                  </div>
                  <ScrollIndicator indicator={scrollIndicator} className="mt-3 h-1" />
                </>
              ) : (
                 // Pie Chart View (excluding D stage)
                 <div className="flex flex-col items-center justify-start h-full w-full">
                   {/* Pie Chart - Fixed position at the top */}
                   <div className="relative w-64 h-64 sm:w-72 sm:h-72 md:w-80 md:h-80 flex-shrink-0 -mt-4">
                     <svg width="80" height="80" viewBox="0 0 80 80" className="w-full h-full">
                       {(() => {
                         // Get pie chart data based on expand state
                         let pieData;
                         if (expandPieChart) {
                           // Show individual stages when expanded
                           const individualStages = [];
                           if (dataSource.lcaStagesData && Object.keys(dataSource.lcaStagesData).length > 0) {
                             // Use actual Excel data when available
                             const stageValues = {};
                             Object.values(dataSource.lcaStagesData).forEach(componentStages => {
                               // Individual stages A1, A2, A3, A4, A5, B1, B2, B3, B4, B5, B6, B7, C1, C2, C3, C4, D
                               ['A1', 'A2', 'A3', 'A4', 'A5', 'B1', 'B2', 'B3', 'B4', 'B5', 'B6', 'B7', 'C1', 'C2', 'C3', 'C4', 'D'].forEach(stage => {
                                 if (componentStages[stage]) {
                                   stageValues[stage] = (stageValues[stage] || 0) + componentStages[stage];
                                 }
                               });
                             });
                             const hasB6Stage = Object.values(dataSource.lcaStagesData).some((stages) => Number(stages?.B6) > 0);
                             if (includeOperationalB6 && Number(b6OperationalImpact) > 0 && !hasB6Stage) {
                               stageValues.B6 = (stageValues.B6 || 0) + Number(b6OperationalImpact);
                             }
                             
                             // Convert to array format for pie chart
                             const stageOrder = ['A1', 'A2', 'A3', 'A4', 'A5', 'B1', 'B2', 'B3', 'B4', 'B5', 'B6', 'B7', 'C1', 'C2', 'C3', 'C4', 'D'];
                             stageOrder.forEach(stageId => {
                               if (stageValues[stageId] && stageValues[stageId] > 0) {
                                 individualStages.push({
                                   id: stageId,
                                   name: stageId,
                                   displayValue: stageValues[stageId],
                                   fullName: getStageFullName(stageId)
                                 });
                               }
                             });
                           } else {
                             // Sample data mode: Create individual stages from grouped stages
                             // Distribute the grouped stage values across individual stages
                             const stageDistributions = {
                               'a1-a3': { stages: ['A1', 'A2', 'A3'], weights: [0.4, 0.3, 0.3] },
                               'a4-a5': { stages: ['A4', 'A5'], weights: [0.6, 0.4] },
                               'b1-b7': { stages: ['B1', 'B2', 'B3', 'B4', 'B5', 'B6', 'B7'], weights: [0.2, 0.15, 0.15, 0.15, 0.15, 0.1, 0.1] },
                               'c1-c4': { stages: ['C1', 'C2', 'C3', 'C4'], weights: [0.3, 0.3, 0.2, 0.2] },
                               'd': { stages: ['D'], weights: [1.0] }
                             };
                             
                             // Process each grouped stage
                             chartData.forEach(groupedStage => {
                               if (groupedStage.displayValue > 0 && stageDistributions[groupedStage.id]) {
                                 const { stages, weights } = stageDistributions[groupedStage.id];
                                 const shouldSplitB6 = groupedStage.id === 'b1-b7' && includeOperationalB6 && Number(b6OperationalImpact) > 0;
                                 const operationalB6 = shouldSplitB6 ? Number(b6OperationalImpact) : 0;
                                 const remainingValue = shouldSplitB6
                                   ? Math.max(0, groupedStage.displayValue - operationalB6)
                                   : groupedStage.displayValue;

                                 if (shouldSplitB6 && operationalB6 > 0) {
                                   individualStages.push({
                                     id: 'B6',
                                     name: 'B6',
                                     displayValue: operationalB6,
                                     fullName: getStageFullName('B6')
                                   });
                                 }

                                 const adjustedStages = shouldSplitB6
                                   ? stages.filter(stageId => stageId !== 'B6')
                                   : stages;
                                 const adjustedWeights = shouldSplitB6
                                   ? weights.filter((_, idx) => stages[idx] !== 'B6')
                                   : weights;
                                 const weightSum = adjustedWeights.reduce((sum, w) => sum + w, 0) || 1;

                                 adjustedStages.forEach((stageId, index) => {
                                   const normalizedWeight = adjustedWeights[index] / weightSum;
                                   const individualValue = remainingValue * normalizedWeight;
                                   if (individualValue > 0) {
                                     individualStages.push({
                                       id: stageId,
                                       name: stageId,
                                       displayValue: individualValue,
                                       fullName: getStageFullName(stageId)
                                     });
                                   }
                                 });
                               }
                             });
                           }
                           pieData = individualStages;
                         } else {
                           // Show grouped stages when collapsed
                           // Include D stage if it has a positive value, otherwise exclude it
                           pieData = chartData.filter(stage => stage.id !== 'd' || stage.displayValue > 0);
                         }
                         
                         // Filter for positive values only (D stage will be included if positive)
                         const positiveStages = pieData.filter(stage => stage.displayValue > 0);
                         
                         let currentAngle = -Math.PI / 2; // Start from top (12 o'clock position)
                         
                         return positiveStages.map((stage, index) => {
                           const percentage = (stage.displayValue / totalPositiveImpact) * 100;
                           const startAngle = currentAngle;
                           const endAngle = currentAngle + (percentage / 100) * 2 * Math.PI;
                           currentAngle = endAngle;
                           
                           // Calculate arc coordinates
                           const radius = 35;
                           const centerX = 40;
                           const centerY = 40;
                           
                           const x1 = centerX + radius * Math.cos(startAngle);
                           const y1 = centerY + radius * Math.sin(startAngle);
                           const x2 = centerX + radius * Math.cos(endAngle);
                           const y2 = centerY + radius * Math.sin(endAngle);
                           
                           // Create SVG path for pie slice
                           const largeArcFlag = percentage > 50 ? 1 : 0;
                           const pathData = [
                             `M ${centerX} ${centerY}`,
                             `L ${x1} ${y1}`,
                             `A ${radius} ${radius} 0 ${largeArcFlag} 1 ${x2} ${y2}`,
                             'Z'
                           ].join(' ');
                           
                           const colors = ['#f59e0b', '#8b5cf6', '#3b82f6', '#ef4444', '#10b981', '#f97316', '#06b6d4', '#84cc16', '#a855f7', '#ec4899', '#14b8a6', '#fbbf24', '#c084fc', '#60a5fa', '#f87171', '#34d399'];
                           
                           return (
                             <path
                               key={stage.id}
                               d={pathData}
                               fill={colors[index % colors.length]}
                               stroke="rgba(255,255,255,0.8)"
                               strokeWidth="0.3"
                               className={`drop-shadow-sm cursor-pointer transition-all ${
                                 hoveredStage && hoveredStage.id === stage.id 
                                   ? 'opacity-100' 
                                   : hoveredStage 
                                     ? 'opacity-40' 
                                     : 'opacity-100 hover:opacity-80'
                               }`}
                               onMouseEnter={() => setHoveredStage(stage)}
                               onMouseLeave={() => setHoveredStage(null)}
                             />
                           );
                         });
                       })()}
                     </svg>
                   </div>
                   
                   {/* Pie Chart Legend - Expands downward from the top */}
                   <div className="flex flex-wrap justify-center gap-2 max-w-full mt-3">
                     {(() => {
                         let pieData;
                         if (expandPieChart) {
                           // Show individual stages when expanded
                           const individualStages = [];
                           if (dataSource.lcaStagesData && Object.keys(dataSource.lcaStagesData).length > 0) {
                            // Aggregate individual LCA stages from Excel data
                            const stageValues = {};
                            Object.values(dataSource.lcaStagesData).forEach(componentStages => {
                               // Individual stages A1, A2, A3, A4, A5, B1, B2, B3, B4, B5, B6, B7, C1, C2, C3, C4, D
                               ['A1', 'A2', 'A3', 'A4', 'A5', 'B1', 'B2', 'B3', 'B4', 'B5', 'B6', 'B7', 'C1', 'C2', 'C3', 'C4', 'D'].forEach(stage => {
                                 if (componentStages[stage]) {
                                   stageValues[stage] = (stageValues[stage] || 0) + componentStages[stage];
                                 }
                               });
                             });
                             
                             // Convert to array format for pie chart
                             const stageOrder = ['A1', 'A2', 'A3', 'A4', 'A5', 'B1', 'B2', 'B3', 'B4', 'B5', 'B6', 'B7', 'C1', 'C2', 'C3', 'C4', 'D'];
                             stageOrder.forEach(stageId => {
                               if (stageValues[stageId] && stageValues[stageId] > 0) {
                                 individualStages.push({
                                   id: stageId,
                                   name: stageId,
                                   displayValue: stageValues[stageId],
                                   fullName: getStageFullName(stageId)
                                 });
                               }
                             });
                           } else {
                             // Sample data mode: Create individual stages from grouped stages
                             // Distribute the grouped stage values across individual stages
                             const stageDistributions = {
                               'a1-a3': { stages: ['A1', 'A2', 'A3'], weights: [0.4, 0.3, 0.3] },
                               'a4-a5': { stages: ['A4', 'A5'], weights: [0.6, 0.4] },
                               'b1-b7': { stages: ['B1', 'B2', 'B3', 'B4', 'B5', 'B6', 'B7'], weights: [0.2, 0.15, 0.15, 0.15, 0.15, 0.1, 0.1] },
                               'c1-c4': { stages: ['C1', 'C2', 'C3', 'C4'], weights: [0.3, 0.3, 0.2, 0.2] },
                               'd': { stages: ['D'], weights: [1.0] }
                             };
                             
                             // Process each grouped stage
                             chartData.forEach(groupedStage => {
                               if (groupedStage.displayValue > 0 && stageDistributions[groupedStage.id]) {
                                 const { stages, weights } = stageDistributions[groupedStage.id];
                                 const shouldSplitB6 = groupedStage.id === 'b1-b7' && includeOperationalB6 && Number(b6OperationalImpact) > 0;
                                 const operationalB6 = shouldSplitB6 ? Number(b6OperationalImpact) : 0;
                                 const remainingValue = shouldSplitB6
                                   ? Math.max(0, groupedStage.displayValue - operationalB6)
                                   : groupedStage.displayValue;

                                 if (shouldSplitB6 && operationalB6 > 0) {
                                   individualStages.push({
                                     id: 'B6',
                                     name: 'B6',
                                     displayValue: operationalB6,
                                     fullName: getStageFullName('B6')
                                   });
                                 }

                                 const adjustedStages = shouldSplitB6
                                   ? stages.filter(stageId => stageId !== 'B6')
                                   : stages;
                                 const adjustedWeights = shouldSplitB6
                                   ? weights.filter((_, idx) => stages[idx] !== 'B6')
                                   : weights;
                                 const weightSum = adjustedWeights.reduce((sum, w) => sum + w, 0) || 1;

                                 adjustedStages.forEach((stageId, index) => {
                                   const normalizedWeight = adjustedWeights[index] / weightSum;
                                   const individualValue = remainingValue * normalizedWeight;
                                   if (individualValue > 0) {
                                     individualStages.push({
                                       id: stageId,
                                       name: stageId,
                                       displayValue: individualValue,
                                       fullName: getStageFullName(stageId)
                                     });
                                   }
                                 });
                               }
                             });
                           }
                           pieData = individualStages;
                         } else {
                           // Show grouped stages when collapsed
                           // Include D stage if it has a positive value, otherwise exclude it
                           pieData = chartData.filter(stage => stage.id !== 'd' || stage.displayValue > 0);
                         }
                         
                         const total = pieData.reduce((sum, stage) => sum + stage.displayValue, 0);
                         const colors = ['#f59e0b', '#8b5cf6', '#3b82f6', '#ef4444', '#10b981', '#f97316', '#06b6d4', '#84cc16', '#a855f7', '#ec4899', '#14b8a6', '#fbbf24', '#c084fc', '#60a5fa', '#f87171', '#34d399'];
                         
                         return pieData.map((stage, index) => {
                           const percentage = (stage.displayValue / total) * 100;
                           
                           return (
                             <div 
                               key={stage.id} 
                               className="flex items-center space-x-1 group cursor-pointer"
                               onMouseEnter={() => setHoveredStage(stage)}
                               onMouseLeave={() => setHoveredStage(null)}
                             >
                               <div 
                                 className="w-2.5 h-2.5 rounded-full shadow-sm transition-transform" 
                                 style={{ backgroundColor: colors[index % colors.length] }}
                               />
                               <span className="text-xs text-gray-700 font-medium">{stage.name}</span>
                               <span className="text-xs text-gray-500">({percentage.toFixed(1)}%)</span>
                             </div>
                           );
                         });
                       })()}
                   </div>
                 </div>
               )}
             </div>
          </div>
        </div>
        
        {/* Detailed Hover Tooltip */}
        {hoveredStage && (
          <div className="fixed top-4 left-4 bg-gray-900 text-white p-2 rounded-lg shadow-xl z-50 min-w-[200px]">
            <div className="font-semibold text-emerald-400 text-sm">
              {hoveredStage.name} - {hoveredStage.fullName}
            </div>
            <div className="text-xs mt-1 space-y-0.5">
              <div className="text-gray-300">{hoveredStage.description}</div>
              <div className="border-t border-gray-700 pt-1 mt-1">
                <div className="font-medium">
                  Impact: {hoveredStage.displayValue >= 1000 ? `${(hoveredStage.displayValue / 1000).toFixed(2)} t` : `${hoveredStage.displayValue.toLocaleString()} ${selectedImpactInfo?.unit}`}
                  {(() => {
                    if (chartType === 'pie') {
                      // For pie chart, calculate percentage based on expand state
                      let pieData;
                      if (expandPieChart) {
                        // Show individual stages when expanded
                        const individualStages = [];
                        if (dataSource.lcaStagesData && Object.keys(dataSource.lcaStagesData).length > 0) {
                          // Use actual Excel data when available
                          const stageValues = {};
                          Object.values(dataSource.lcaStagesData).forEach(componentStages => {
                            // Individual stages A1, A2, A3, A4, A5, B1, B2, B3, B4, B5, B6, B7, C1, C2, C3, C4, D
                            ['A1', 'A2', 'A3', 'A4', 'A5', 'B1', 'B2', 'B3', 'B4', 'B5', 'B6', 'B7', 'C1', 'C2', 'C3', 'C4', 'D'].forEach(stage => {
                              if (componentStages[stage]) {
                                stageValues[stage] = (stageValues[stage] || 0) + componentStages[stage];
                              }
                            });
                            const hasB6Stage = Object.values(dataSource.lcaStagesData).some((stages) => Number(stages?.B6) > 0);
                            if (!includeOperationalB6 && hasB6Stage) {
                              stageValues.B6 = 0;
                            }
                          });
                          const hasB6Stage = Object.values(dataSource.lcaStagesData).some((stages) => Number(stages?.B6) > 0);
                          if (!includeOperationalB6 && hasB6Stage) {
                            stageValues.B6 = 0;
                          }
                          if (includeOperationalB6 && Number(b6OperationalImpact) > 0 && !hasB6Stage) {
                            stageValues.B6 = (stageValues.B6 || 0) + Number(b6OperationalImpact);
                          }
                          
                          // Convert to array format for pie chart
                          const stageOrder = ['A1', 'A2', 'A3', 'A4', 'A5', 'B1', 'B2', 'B3', 'B4', 'B5', 'B6', 'B7', 'C1', 'C2', 'C3', 'C4', 'D'];
                          stageOrder.forEach(stageId => {
                            if (stageValues[stageId] && stageValues[stageId] > 0) {
                              individualStages.push({
                                id: stageId,
                                name: stageId,
                                displayValue: stageValues[stageId],
                                fullName: getStageFullName(stageId)
                              });
                            }
                          });
                          } else {
                          // Sample data mode: Create individual stages from grouped stages
                          // Distribute the grouped stage values across individual stages
                          const stageDistributions = {
                            'a1-a3': { stages: ['A1', 'A2', 'A3'], weights: [0.4, 0.3, 0.3] },
                            'a4-a5': { stages: ['A4', 'A5'], weights: [0.6, 0.4] },
                            'b1-b7': { stages: ['B1', 'B2', 'B3', 'B4', 'B5', 'B6', 'B7'], weights: [0.2, 0.15, 0.15, 0.15, 0.15, 0.1, 0.1] },
                            'c1-c4': { stages: ['C1', 'C2', 'C3', 'C4'], weights: [0.3, 0.3, 0.2, 0.2] },
                            'd': { stages: ['D'], weights: [1.0] }
                          };
                          
                          // Process each grouped stage
                            chartData.forEach(groupedStage => {
                              if (groupedStage.displayValue > 0 && stageDistributions[groupedStage.id]) {
                                const { stages, weights } = stageDistributions[groupedStage.id];
                                const shouldSplitB6 = groupedStage.id === 'b1-b7' && includeOperationalB6 && Number(b6OperationalImpact) > 0;
                                const operationalB6 = shouldSplitB6 ? Number(b6OperationalImpact) : 0;
                                const remainingValue = shouldSplitB6
                                  ? Math.max(0, groupedStage.displayValue - operationalB6)
                                  : groupedStage.displayValue;

                                if (shouldSplitB6 && operationalB6 > 0) {
                                  individualStages.push({
                                    id: 'B6',
                                    name: 'B6',
                                    displayValue: operationalB6,
                                    fullName: getStageFullName('B6')
                                  });
                                }

                                const adjustedStages = shouldSplitB6
                                  ? stages.filter(stageId => stageId !== 'B6')
                                  : stages;
                                const adjustedWeights = shouldSplitB6
                                  ? weights.filter((_, idx) => stages[idx] !== 'B6')
                                  : weights;
                                const weightSum = adjustedWeights.reduce((sum, w) => sum + w, 0) || 1;

                                adjustedStages.forEach((stageId, index) => {
                                  const normalizedWeight = adjustedWeights[index] / weightSum;
                                  const individualValue = remainingValue * normalizedWeight;
                                  if (individualValue > 0) {
                                    individualStages.push({
                                      id: stageId,
                                      name: stageId,
                                      displayValue: individualValue,
                                      fullName: getStageFullName(stageId)
                                    });
                                  }
                                });
                              }
                            });
                        }
                        pieData = individualStages;
                      } else {
                        // Show grouped stages when collapsed
                        // Include D stage if it has a positive value, otherwise exclude it
                        pieData = chartData.filter(stage => stage.id !== 'd' || stage.displayValue > 0);
                      }
                      
                      const total = pieData.reduce((sum, stage) => sum + stage.displayValue, 0);
                      const percentage = (hoveredStage.displayValue / total) * 100;
                      return ` (${percentage.toFixed(1)}%)`;
                    } else {
                      // For bar chart, calculate percentage of total building impact
                      const total = chartData.reduce((sum, stage) => sum + Math.abs(stage.displayValue), 0);
                      const percentage = (Math.abs(hoveredStage.displayValue) / total) * 100;
                      return ` (${percentage.toFixed(1)}%)`;
                    }
                  })()}
                  </div>
              </div>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default LifeCycleChart;
